import {
  Alert,
  Box,
  Button,
  Container,
  IconButton,
  InputAdornment,
  Paper,
  Snackbar,
  TextField,
  Typography,
  useTheme,
} from "@mui/material";
import React, { useState } from "react";
import { useNavigate } from "react-router-dom";
import { tokens } from "../../../theme";
import { motion } from "framer-motion";
import VisibilityIcon from "@mui/icons-material/Visibility";
import VisibilityOffIcon from "@mui/icons-material/VisibilityOff";
import EmailIcon from "@mui/icons-material/Email";
import LockIcon from "@mui/icons-material/Lock";
import AdminPanelSettingsIcon from "@mui/icons-material/AdminPanelSettings";
import superAdminService from "../../../services/SüperAdminServices/superAdminService";

export default function SuperAdminLogin() {
  const theme = useTheme();
  const colors = tokens(theme.palette.mode);
  const navigate = useNavigate();
  const [showPassword, setShowPassword] = useState(false);
  const [openSnackBar, setOpenSnackBar] = useState(false);
  const [formData, setFormData] = useState({
    email: "",
    password: "",
  });
  const [error, setError] = useState("");
  const [loading, setLoading] = useState(false);

  const handleChange = (e) => {
    setFormData({
      ...formData,
      [e.target.name]: e.target.value,
    });
    setError("");
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError("");
    setLoading(true);
    
    // Validation: Email and password check
    if (!formData.email || !formData.email.trim()) {
      setError("Please enter your email address");
      setLoading(false);
      return;
    }
    
    if (!formData.password || !formData.password.trim()) {
      setError("Please enter your password");
      setLoading(false);
      return;
    }
    
    // Email format check
    const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    if (!emailRegex.test(formData.email)) {
      setError("Please enter a valid email address");
      setLoading(false);
      return;
    }
    
    try {
      const response = await superAdminService.login(formData.email, formData.password);
      const { token, user } = response;

      if (token) {
        localStorage.setItem("superAdminToken", token);
        localStorage.setItem("superAdminUser", JSON.stringify(user));
        setOpenSnackBar(true);
        setTimeout(() => {
          navigate("/super-admin/dashboard");
        }, 1000);
      }
    } catch (err) {
      setError(err.response?.data?.error || err.response?.data?.message || "An error occurred during login");
    } finally {
      setLoading(false);
    }
  };

  return (
    <Box sx={{ 
      width: "100%",
      minHeight: "100vh",
      background: theme.palette.mode === "dark"
        ? `linear-gradient(135deg, ${colors.primary[600]}, ${colors.blueAccent[700]})`
        : `linear-gradient(135deg, ${colors.primary[400]}, ${colors.blueAccent[500]})`,
      display: "flex",
      alignItems: "center",
      justifyContent: "center",
      py: 4,
    }}>
      <Container maxWidth="sm">
        <motion.div
          initial={{ opacity: 0, y: -20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.5 }}
        >
          <Paper
            elevation={3}
            sx={{
              p: 4,
              borderRadius: "24px",
              background: theme.palette.mode === "dark" ? colors.primary[400] : "rgba(255, 255, 255, 0.95)",
              backdropFilter: "blur(10px)",
              boxShadow: "0 8px 32px rgba(0,0,0,0.1)",
              border: "1px solid rgba(255, 255, 255, 0.1)",
              color: theme.palette.mode === "dark" ? 'white' : colors.grey[100],
              transition: "transform 0.3s ease",
              "&:hover": {
                transform: "translateY(-5px)",
                boxShadow: "0 12px 40px rgba(0,0,0,0.15)",
              },
            }}
          >
            <Box sx={{ display: "flex", justifyContent: "center", mb: 3 }}>
              <AdminPanelSettingsIcon 
                sx={{ 
                  fontSize: 64, 
                  color: colors.blueAccent[500],
                  background: `linear-gradient(135deg, ${colors.blueAccent[400]}, ${colors.blueAccent[600]})`,
                  borderRadius: "50%",
                  p: 1.5,
                }} 
              />
            </Box>

            <Typography
              variant="h3"
              sx={{
                textAlign: "center",
                mb: 1,
                fontFamily: "'Poppins', sans-serif",
                fontWeight: 600,
                background: `linear-gradient(135deg, ${colors.blueAccent[500]}, ${colors.greenAccent[500]})`,
                WebkitBackgroundClip: "text",
                WebkitTextFillColor: "transparent",
              }}
            >
              Super Admin
            </Typography>

            <Typography
              variant="body2"
              sx={{
                textAlign: "center",
                mb: 4,
                color: theme.palette.mode === "dark" ? colors.grey[300] : colors.grey[600],
              }}
            >
              System Administration Panel
            </Typography>

            <form onSubmit={handleSubmit}>
              <Box sx={{ mb: 3 }}>
                <Typography 
                  variant="body2" 
                  sx={{ 
                    mb: 1, 
                    color: theme.palette.mode === "dark" ? "white" : colors.grey[900],
                    fontWeight: 500,
                  }}
                >
                  Email Address
                </Typography>
                <TextField
                  fullWidth
                  type="email"
                  name="email"
                  placeholder="admin@example.com"
                  value={formData.email}
                  onChange={handleChange}
                  required
                  disabled={loading}
                  InputProps={{
                    startAdornment: (
                      <InputAdornment position="start">
                        <EmailIcon sx={{ color: theme.palette.mode === "dark" ? "rgba(255, 255, 255, 0.7)" : colors.grey[500] }} />
                      </InputAdornment>
                    ),
                  }}
                  sx={{
                    "& .MuiOutlinedInput-root": {
                      borderRadius: "12px",
                      backgroundColor: theme.palette.mode === "dark" ? colors.primary[500] : "white",
                      "&:hover fieldset": {
                        borderColor: colors.blueAccent[500],
                      },
                    },
                    "& .MuiInputBase-input": {
                      color: theme.palette.mode === "dark" ? "white" : colors.grey[900],
                    },
                  }}
                />
              </Box>

              <Box sx={{ mb: 4 }}>
                <Typography 
                  variant="body2" 
                  sx={{ 
                    mb: 1, 
                    color: theme.palette.mode === "dark" ? "white" : colors.grey[900],
                    fontWeight: 500,
                  }}
                >
                  Password
                </Typography>
                <TextField
                  fullWidth
                  name="password"
                  placeholder="Enter your password"
                  type={showPassword ? "text" : "password"}
                  value={formData.password}
                  onChange={handleChange}
                  required
                  disabled={loading}
                  InputProps={{
                    startAdornment: (
                      <InputAdornment position="start">
                        <LockIcon sx={{ color: theme.palette.mode === "dark" ? "rgba(255, 255, 255, 0.7)" : colors.grey[500] }} />
                      </InputAdornment>
                    ),
                    endAdornment: (
                      <InputAdornment position="end">
                        <IconButton
                          onClick={() => setShowPassword(!showPassword)}
                          edge="end"
                          disabled={loading}
                        >
                          {showPassword ? (
                            <VisibilityOffIcon sx={{ color: theme.palette.mode === "dark" ? "rgba(255, 255, 255, 0.7)" : colors.grey[500] }} />
                          ) : (
                            <VisibilityIcon sx={{ color: theme.palette.mode === "dark" ? "rgba(255, 255, 255, 0.7)" : colors.grey[500] }} />
                          )}
                        </IconButton>
                      </InputAdornment>
                    ),
                  }}
                  sx={{
                    "& .MuiOutlinedInput-root": {
                      borderRadius: "12px",
                      backgroundColor: theme.palette.mode === "dark" ? colors.primary[500] : "white",
                      "&:hover fieldset": {
                        borderColor: colors.blueAccent[500],
                      },
                    },
                    "& .MuiInputBase-input": {
                      color: theme.palette.mode === "dark" ? "white" : colors.grey[900],
                    },
                  }}
                />
              </Box>

              {error && (
                <Alert
                  severity="error"
                  sx={{ 
                    mb: 2, 
                    borderRadius: "12px",
                  }}
                >
                  {error}
                </Alert>
              )}

              <Button
                type="submit"
                fullWidth
                variant="contained"
                disabled={loading}
                sx={{
                  background: `linear-gradient(135deg, ${colors.blueAccent[500]}, ${colors.blueAccent[600]})`,
                  color: "white",
                  borderRadius: "12px",
                  padding: "12px",
                  fontSize: "1rem",
                  fontWeight: 600,
                  textTransform: "none",
                  "&:hover": {
                    background: `linear-gradient(135deg, ${colors.blueAccent[600]}, ${colors.blueAccent[700]})`,
                  },
                  "&:disabled": {
                    background: colors.grey[500],
                  },
                }}
              >
                {loading ? "Logging in..." : "Login"}
              </Button>
            </form>
          </Paper>
        </motion.div>
      </Container>

      <Snackbar
        open={openSnackBar}
        autoHideDuration={3000}
        onClose={() => setOpenSnackBar(false)}
        anchorOrigin={{
          vertical: "bottom",
          horizontal: "right",
        }}
      >
        <Alert
          onClose={() => setOpenSnackBar(false)}
          severity="success"
          variant="filled"
          sx={{
            width: "100%",
            borderRadius: "12px",
            backgroundColor: colors.greenAccent[500],
          }}
        >
          <Typography variant="body1" color="white">
            Login successful! Redirecting...
          </Typography>
        </Alert>
      </Snackbar>
    </Box>
  );
}

